import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { client, urlFor } from '../lib/sanity/client';
import AuthorBio from './AuthorBio';

type HeaderData = {
  title: string;
  tagline: string;
  description: string;
  backgroundImage: any;
  capitalCurrents: {
    title: string;
    description: string;
    stats: Array<{
      value: number;
      label: string;
      prefix?: string;
      suffix?: string;
    }>;
  };
};

type AnimatedStatProps = { value: number; label: string; suffix?: string; prefix?: string };
function AnimatedStat({ value, label, suffix = '', prefix = '' }: AnimatedStatProps) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  useEffect(() => {
    if (inView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [inView, value]);
  return (
    <div
      ref={ref}
      className="flex flex-col items-center justify-center aspect-square w-40 md:w-52 bg-white/70 rounded-full shadow-xl border-2 border-blue-100/40 backdrop-blur-md mx-auto mb-6 hover:shadow-2xl hover:scale-105 transition-all duration-300"
      style={{ minHeight: '10rem' }}
    >
      <div className="text-4xl md:text-5xl font-serif text-blue-900 mb-1 font-extrabold drop-shadow-lg">
        {prefix}{Math.round(count)}{suffix}
      </div>
      <div className="text-sm md:text-base tracking-wider text-blue-800/80 uppercase font-semibold text-center px-2">
        {label}
      </div>
    </div>
  );
}

export default function Header() {
  const [headerData, setHeaderData] = useState<HeaderData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        const query = `*[_type == "header"][0] {
          title,
          tagline,
          description,
          backgroundImage,
          capitalCurrents
        }`;
        const data = await client.fetch(query);
        setHeaderData(data);
      } catch (error) {
        console.error('Error fetching header data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeaderData();
  }, []);

  const scrollToArticles = () => {
    const articlesSection = document.getElementById('articles');
    articlesSection?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading || !headerData) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <header className="relative min-h-screen flex flex-col justify-start text-white overflow-hidden">
      <AuthorBio />
      <div 
        className="absolute inset-0 bg-cover bg-top bg-no-repeat"
        style={{
          backgroundImage: `url(${urlFor(headerData.backgroundImage).url()})`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-blue-900/60 to-blue-800/80" />
      {/* Accent Shape */}
      <div className="absolute -left-32 -top-32 w-[500px] h-[500px] bg-gradient-to-tr from-blue-400/30 via-blue-200/10 to-transparent rounded-full blur-3xl z-0" />
      <motion.div
        className="relative z-10 text-left max-w-3xl px-8 pt-[47vh] pb-16 md:ml-16"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="text-[5rem] md:text-[8rem] font-extrabold mb-8 leading-none drop-shadow-2xl text-left tracking-wide uppercase bg-clip-text text-transparent bg-gradient-to-r from-blue-200 via-white to-blue-400 font-sans"
          style={{ letterSpacing: '0.04em', fontFamily: 'Inter, Arial, sans-serif' }}
        >
          {headerData.title}
        </motion.h1>
        
          <p className="text-xl md:text-2xl mb-12 text-blue-100 max-w-xl leading-relaxed font-light">
            {headerData.description}
          </p>
        <motion.button
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: 'easeOut' }}
          onClick={scrollToArticles}
          className="group flex items-center gap-3 text-lg text-blue-900 bg-blue-200 hover:bg-white hover:text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-all border-2 border-blue-100/30 backdrop-blur-md"
        >
          <span className="tracking-wide">Explore Insights</span>
          <ChevronDown className="w-7 h-7 group-hover:translate-y-2 transition-transform duration-300" />
        </motion.button>
      </motion.div>
      {/* Capital Currents Section */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center pb-24 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative"
        >
          <h2 className="text-4xl md:text-5xl font-serif text-center mb-6 bg-gradient-to-r from-blue-200 via-white to-blue-300 bg-clip-text text-transparent drop-shadow-xl">
            {headerData.capitalCurrents.title} <span className="text-blue-200">~</span> 2025
          </h2>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-blue-200 to-transparent" />
        </motion.div>
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="text-lg md:text-2xl text-blue-100 font-medium leading-relaxed drop-shadow">
            {headerData.capitalCurrents.description}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
          {headerData.capitalCurrents.stats.map((stat, index) => (
            <AnimatedStat
              key={index}
              value={stat.value}
              label={stat.label}
              prefix={stat.prefix}
              suffix={stat.suffix}
            />
          ))}
        </div>
      </div>
    </header>
  );
}
